import { useTranslation } from "react-i18next";
import { FieldArray, Formik } from "formik";
import * as yup from "yup";
import { AntSelect, Field, Select } from "../../../../components";
import { useStore } from "../../../../stores";
import locale from "../../../../constants/locale";
import { CampaignDataType, CampaignFileDraft, Chat, ChatChannel, WhatsappTemplate } from "../../../../models";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getChats } from "../../../../services/chathub";
import { TagsInputV2 } from "../../../../components/tagsInput/TagsInputV2";
import { campaignMetaPrices } from "../../../../utils/campaignMetaPrices";
import { observer } from "mobx-react-lite";
import { FiUploadCloud, FiUsers, FiTag, FiGitBranch, FiCheck } from "react-icons/fi";
import styles from "../CampaignAudience.module.scss";
import CampaignFileUpload, { blankMappings, initialConfiguration } from "../CampaignFileUpload";

export const SYSTEM_PREDEIFINED_FIELDS = ["FIRST_NAME", "LAST_NAME", "PHONE"];

export const extractVariablesFromString = (text: string) => {
  const regex = /{{\s*(.*?)\s*}}/g;
  const matches = text.match(regex);

  return matches ?? [];
};

const UploadAudience = ({ setStep, campaign, messageTemplates, serverError }: any) => {
  const { t } = useTranslation();
  const { labels, loadLabels, selectedBot, activeLiveChatCards } =
    useStore("bots");
  const { boards, selectedBoard, loadBoards, selectBoard } = useStore("boards");
  const [contacts, setContacts] = useState<Chat[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const contactsRequest = useRef(0);
  const [messageTemplateFields, setMessageTemplateFields] = useState<string[]>(
    []
  );
  const [fileDraft, setFileDraft] = useState<CampaignFileDraft>(() => {
    const saved = campaign?.fileDraft as CampaignFileDraft | undefined;
    if (saved && saved.botId === campaign?.botId && saved.channel === campaign?.channel) return saved;
    return {
      botId: campaign?.botId || 0,
      channel: campaign?.channel || "",
      file: campaign?.file,
      configuration: initialConfiguration(),
      fieldsMapping: blankMappings(campaign?.template?.messageBody || campaign?.message || ""),
      preview: null,
    };
  });

  const storedData = useMemo(() => {
    try {
      return typeof campaign?.data === "string" ? JSON.parse(campaign.data) : {};
    } catch {
      return {};
    }
  }, [campaign?.data]);
  const savedTemplate = campaign?.template || storedData?.template;
  const savedContactMappings = campaign?.contactFieldsMapping || (storedData?.fieldsMapping || []).filter((mapping: any) => !mapping.kind);
  const savedAudienceMode = campaign?.audienceMode || storedData?.audienceMode || (storedData?.tag ? "tag" : storedData?.boardId ? "pipeline" : campaign?.campaignDataType);
  const savedTag = campaign?.tag || storedData?.tag;
  const selectedBotForDraft = selectedBot?.id || 0;
  useEffect(() => {
    const message = campaign?.message || storedData?.message || savedTemplate?.messageBody || "";
    setMessageTemplateFields(extractVariablesFromString(message));
  }, [campaign?.message, storedData?.message, savedTemplate?.messageBody]);

  useEffect(() => {
    if (selectedBotForDraft && (fileDraft.botId !== selectedBotForDraft || fileDraft.channel !== campaign?.channel)) {
      setFileDraft({
        botId: selectedBotForDraft,
        channel: campaign?.channel || "",
        file: undefined,
        configuration: initialConfiguration(),
        fieldsMapping: blankMappings(savedTemplate?.messageBody || ""),
        preview: null,
      });
    }
  }, [selectedBotForDraft, campaign?.channel, fileDraft.botId, fileDraft.channel, savedTemplate?.messageBody]);

  useEffect(() => {
    if (!selectedBot) {
      return;
    }

    if (!labels?.length) {
      loadLabels();
    }
  }, [selectedBot, labels.length, loadLabels, labels]);

  useEffect(() => {
    if (!selectedBot?.id) {
      return;
    }

    loadBoards();
  }, [loadBoards, selectedBot?.id]);

  const handleLabelSelect = useCallback(
    async (label?: string | null) => {
      const request = ++contactsRequest.current;
      setContacts([]);
      if (!selectedBot || !label) {
        setContactsLoading(false);
        return;
      }

      setContactsLoading(true);
      try {
        const { chats } = await getChats(
          undefined,
          selectedBot.id,
          selectedBot.key,
          selectedBot.password,
          undefined,
          label,
          "all",
          undefined,
          1000000,
          undefined,
          undefined,
          undefined,
          campaign?.channel,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        );

        if (request === contactsRequest.current) {
          setContacts((chats as Chat[]).filter((f) => f.channel === campaign?.channel));
        }
      } catch {
        if (request === contactsRequest.current) setContacts([]);
      } finally {
        if (request === contactsRequest.current) setContactsLoading(false);
      }
    },
    [selectedBot, campaign?.channel]
  );

  useEffect(() => {
    if (savedAudienceMode === "tag" && savedTag) {
      void handleLabelSelect(savedTag);
    } else {
      contactsRequest.current++;
      setContacts([]);
      setContactsLoading(false);
    }
  }, [savedAudienceMode, savedTag, handleLabelSelect]);

  useEffect(() => () => { contactsRequest.current++; }, []);

  const contactsTableHeader = useMemo(
    () =>
      selectedBot?.customization?.contactsTable.header?.filter(
        (f) =>
          ![
            "firstName",
            "lastName",
            "channel",
            "createdAt",
            "lastMessage",
          ].includes(f)
      ) || [],
    [selectedBot?.customization?.contactsTable.header]
  );

  const validationSchema = useMemo(() => {
    const mappingSchema = yup.array().of(
      yup.object().shape({
        source: yup.string().required(t(locale.fieldRequired)),
        value: yup.string().required(t(locale.fieldRequired)),
      })
    );
    return yup.object().shape({
      targetAudienceCountry: yup.string().required(t(locale.fieldRequired)),
      templateType: yup.string().required(t(locale.fieldRequired)),
      messageTemplate: yup.string().required(t(locale.fieldRequired)),
      livecardId: yup.string().required(t(locale.fieldRequired)),
      campaignDataType: yup.string().required(t(locale.fieldRequired)),
      tag: yup.string().when("campaignDataType", {
        is: (d: string) => d === "tag",
        then: yup.string().required(t(locale.fieldRequired)),
        otherwise: yup.string().nullable().optional(),
      }),
      boardId: yup.string().when("campaignDataType", {
        is: (d: string) => d === "pipeline",
        then: yup.string().required(t(locale.fieldRequired)),
        otherwise: yup.string().nullable().optional(),
      }),
      columnId: yup.string().when("campaignDataType", {
        is: (d: string) => d === "pipeline",
        then: yup.string().required(t(locale.fieldRequired)),
        otherwise: yup.string().nullable().optional(),
      }),
      fieldsMapping: yup.array().when("campaignDataType", {
        is: (dataType: string) => dataType === "file",
        then: yup.array().optional(),
        otherwise: mappingSchema.required(t(locale.fieldRequired)),
      }),
      message: yup.mixed().when("campaignDataType", {
        is: (d: string) => d === "json",
        then: yup.mixed().required(t(locale.fieldRequired)),
      }),
      recipients: yup.array(yup.string().required(t(locale.fieldRequired))).required(t(locale.fieldRequired)),
      data: yup.mixed().when("campaignDataType", {
        is: (d: string) => d === "file",
        then: yup.mixed().required(t(locale.fieldRequired)),
      }),
    });
  }, [t]);

  return (
    <div className={`step-content is-active ${styles.audience}`}>
      <Formik
        initialValues={{
          targetAudienceCountry: campaign?.targetAudienceCountry || storedData?.meta?.targetAudienceCountry,
          templateType: campaign?.templateType || storedData?.meta?.templateType || savedTemplate?.category,
          message: campaign?.message || storedData?.message || savedTemplate?.messageBody,
          messageTemplate: savedTemplate?.id,
          livecardId: campaign?.livecardId || storedData?.livecardId,
          template: savedTemplate,
          recipients: storedData?.recipients || [],
          tag: storedData?.tag || "",
          boardId: storedData?.boardId,
          columnId: storedData?.columnId,
          campaignDataType: savedAudienceMode || "file" as CampaignDataType,
          data: campaign?.fileDraft?.file || campaign?.file || (undefined as unknown as File),
          fieldsMapping: savedContactMappings || [],
        }}
        validationSchema={validationSchema}
        onSubmit={async (
          {
            targetAudienceCountry,
            templateType,
            data,
            campaignDataType,
            recipients,
            message,
            template,
            fieldsMapping,
            livecardId,
            boardId,
            columnId,
            messageTemplate,
            tag,
        },
          helpers
        ) => {
          if (!selectedBot || (campaignDataType === "tag" && contactsLoading)) {
            return;
          }
          const isFile = campaignDataType === "file";
          if (isFile && (fileDraft.previewStale || !fileDraft.preview?.canLaunch || !fileDraft.preview.revision)) {
            return;
          }
          const campaignFieldsMapping = isFile ? fileDraft.fieldsMapping : fieldsMapping;
          const campaignData = {
            message,
            template,
            fieldsMapping: campaignFieldsMapping,
            livecardId,
            audienceMode: campaignDataType,
            meta: { templateType, targetAudienceCountry },
            tag: campaignDataType === "tag" ? tag : undefined,
            recipients: campaignDataType === "tag"
              ? contacts.map((f) => f.data?.fb_id)
              : campaignDataType === "json" ? recipients : undefined,
            boardId: campaignDataType === "pipeline" ? boardId : undefined,
            columnId: campaignDataType === "pipeline" ? columnId : undefined,
            ...(isFile ? {
              audienceFile: fileDraft.configuration,
              previewRevision: fileDraft.preview?.revision,
            } : {}),
          };

          setStep(1, {
            file: campaignDataType === "file" ? data : undefined,
            fileDraft: {
              ...fileDraft,
              file: campaignDataType === "file" ? data : fileDraft.file,
            },
            audienceMode: campaignDataType,
            botId: selectedBot?.id,
            message,
            template,
            contactFieldsMapping: fieldsMapping,
            livecardId,
            livecardName: activeLiveChatCards.find((card: any) => String(card.id) === String(livecardId))?.name,
            data: JSON.stringify(campaignData),
            campaignDataType: campaignDataType === "file" ? "file" : "json",
            templateType,
            targetAudienceCountry,
            serverError: undefined,
          });
        }}
      >
        {({ submitForm, values, setFieldValue }) => (
          <>
            <div>
              <section className={styles.settings}>
                <h3 className={styles.sectionTitle}>{t(locale.campaignAudienceSettings)}</h3>
              <div className="columns mb-0">
                <div className="column is-4">
                  <Select
                    name="targetAudienceCountry"
                    label={t(locale.targetAudienceCountry)}
                    options={campaignMetaPrices}
                    className="custom-select"
                    getLabel={(l) => l.Market}
                    getValue={(l) => l.Market}
                    extras={<p className="help">{t(locale.campaignHelpCountry)}</p>}
                  />
                </div>
                <div className="column is-4">
                  <AntSelect
                    name="messageTemplate"
                    label={t(locale.messageTemplate)}
                    options={messageTemplates}
                    showSearch
                    filterOption={(input: any, option: any) =>
                      (option?.name ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    fieldNames={{ value: "id", label: "name" }}
                    onChange={(v) => {
                      const messageTemplate = messageTemplates.find(
                        (f: WhatsappTemplate) => f.id === v
                      ) as WhatsappTemplate;
                      if (messageTemplate) {
                        setFieldValue("templateType", messageTemplate.category);
                        setFieldValue("template", messageTemplate);
                        setFieldValue("message", messageTemplate.messageBody);
                        const fields = extractVariablesFromString(
                          messageTemplate.messageBody
                        );
                        setFieldValue(
                          "fieldsMapping",
                          fields.map((f) => ({ source: f, value: undefined }))
                        );
                        setMessageTemplateFields(fields);
                        setFileDraft((current) => {
                          const nextMappings = blankMappings(messageTemplate.messageBody).map((mapping) => {
                            const previous = current.fieldsMapping.find((item) => item.source.replace(/\s+/g, "") === mapping.source.replace(/\s+/g, ""));
                            return previous ? { ...previous, source: mapping.source } : mapping;
                          });
                          return { ...current, fieldsMapping: nextMappings, previewStale: Boolean(current.preview) };
                        });
                      }
                    }}
                    className="custom-select"
                  />
                  <p className="help">{t(locale.campaignHelpTemplate)}</p>
                </div>
                <div className="column is-4">
                  <AntSelect
                    name="livecardId"
                    label={t(locale.assignToGroup)}
                    options={activeLiveChatCards}
                    showSearch
                    filterOption={(input: any, option: any) =>
                      (option?.name ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    fieldNames={{ value: "id", label: "name" }}
                    className="custom-select"
                  />
                  <p className="help">{t(locale.campaignHelpGroup)}</p>
                </div>
              </div>

              </section>

              <fieldset className={styles.sourceSection}>
                <legend className={styles.sectionTitle}>{t(locale.campaignAudienceSource)}</legend>
                <p className={styles.subtitle}>{t(locale.campaignAudienceSourceHelp)}</p>
                <div className={styles.sourceGrid}>
                  {([
                    { value: "file", label: locale.campaignAudienceExcel, hint: locale.campaignAudienceExcelHint, Icon: FiUploadCloud },
                    { value: "json", label: locale.manualInput, hint: locale.campaignAudienceManualHint, Icon: FiUsers },
                    { value: "tag", label: locale.label, hint: locale.campaignAudienceLabelHint, Icon: FiTag },
                    { value: "pipeline", label: locale.pipeline, hint: locale.campaignAudiencePipelineHint, Icon: FiGitBranch },
                  ] as const).map(({ value, label, hint, Icon }) => (
                    <label key={value} className={`${styles.sourceCard} ${values.campaignDataType === value ? styles.sourceSelected : ""}`}>
                      <input type="radio" name="campaignDataType" value={value} checked={values.campaignDataType === value} onChange={() => setFieldValue("campaignDataType", value)} />
                      <Icon className={styles.sourceIcon} aria-hidden="true" />
                      <span><strong>{t(label)}</strong><small>{t(hint)}</small></span>
                      {values.campaignDataType === value && <FiCheck className={styles.sourceCheck} aria-hidden="true" />}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className={styles.sourceContent}>
                <div className={values.campaignDataType === "file" ? styles.fileContent : styles.existingAudience}>
                  {values.campaignDataType === "file" ? (
                    selectedBot ? (
                      <CampaignFileUpload
                        file={values.data}
                        setFile={(file) => {
                          setFieldValue("data", file);
                          if (!file) setFileDraft((current) => ({ ...current, file: undefined, preview: null }));
                        }}
                        channel={campaign.channel as ChatChannel}
                        template={values.template}
                        message={values.message || ""}
                        draft={{
                          ...fileDraft,
                          botId: selectedBot.id,
                          channel: campaign.channel,
                          file: values.data,
                        }}
                        setDraft={setFileDraft}
                        bot={selectedBot}
                        contactFields={[...SYSTEM_PREDEIFINED_FIELDS, ...contactsTableHeader]}
                        serverError={serverError}
                      />
                    ) : <p role="status" className="help">{t(locale.loading)}</p>
                  ) : (
                    <>
                      {(values.campaignDataType as string) === "tag" ? (
                        <>
                          <Select
                            name="tag"
                            label={t(locale.label)}
                            options={labels}
                            allowEmpty
                            className="custom-select"
                            getLabel={(l) => {
                              return l.name;
                            }}
                            getValue={(v) => v.id}
                            onChange={handleLabelSelect}
                            extras={<p className="help">{t(locale.campaignHelpLabel)}</p>}
                          />
                        </>
                      ) : (values.campaignDataType as string) === "pipeline" ? (
                        <>
                          <AntSelect
                            size="large"
                            name="boardId"
                            showSearch
                            filterOption={(input: any, option: any) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            label={t(locale.pipeline)}
                            allowClear
                            onChange={(boardId: any) => {
                              const board = boards?.find(
                                (f) => f.id === boardId
                              );
                              if (board) {
                                selectBoard(board?.uuid);
                              }
                            }}
                            options={
                              boards?.map((f) => ({
                                value: f.id,
                                label: f.name,
                              })) || []
                            }
                          />
                          <p className="help">{t(locale.campaignHelpPipeline)}</p>
                        </>
                      ) : (
                        <>
                        <TagsInputV2
                          name="recipients"
                          label={t(locale.recipients)}
                          className="custom-select"
                        />
                        <p className="help">{t(locale.campaignHelpManual)}</p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {(values.campaignDataType as string) === "pipeline" && (
                <div className="columns px-4">
                  <div className="column is-half">
                    <AntSelect
                      size="large"
                      showSearch
                      filterOption={(input: any, option: any) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      name="columnId"
                      label={t(locale.stage)}
                      options={
                        selectedBoard?.columns?.map((f) => ({
                          value: f.id,
                          label: f.name,
                        })) || []
                      }
                    />
                    <p className="help">{t(locale.campaignHelpStage)}</p>
                  </div>
                </div>
              )}

              {(values.campaignDataType as string) !== "file" && (
                <div className="columns px-4 pb-4">
                  <div className="column">
                    {(values.campaignDataType as string) === "tag" && (
                      <strong>
                        {t(locale.contacts)}: {contacts.length}
                      </strong>
                    )}
                    <Field name="message" type="hidden" />
                  </div>
                </div>
              )}
              {messageTemplateFields?.length > 0 && (values.campaignDataType as string) !== "file" && (
                <>
                  <h4 className="subtitle px-4 mb-2">{t(locale.fieldsMapping)}</h4>
                  <p className="help px-4 mb-3">{t(locale.campaignHelpContactMapping)}</p>

                  <div className="columns px-4 pb-4">
                    <div className="column is-half">
                      <FieldArray
                        name="fieldsMapping"
                        render={() => (
                          <div>
                            {messageTemplateFields.map(
                              (field: any, index: number) => (
                                <div key={index} className="columns">
                                  <div className="column is-half">
                                    <Field
                                      name={`fieldsMapping.${index}.source`}
                                      readOnly={true}
                                      label={t(locale.campaignFileTemplateVariables)}
                                    />
                                  </div>
                                  <div className="column is-half">
                                    <Field
                                      name={`fieldsMapping.${index}.value`}
                                      label={t(locale.campaignFileContactField)}
                                      options={[
                                        ...SYSTEM_PREDEIFINED_FIELDS,
                                        ...contactsTableHeader,
                                      ]}
                                    />
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className="button has-text-weight-medium py-5"
                style={{ fontSize: 13 }}
                onClick={() => setStep(-1)}
              >
                {t(locale.cancel)}
              </button>

              <button
                type="button"
                className="button is-primary has-text-weight-medium py-5 px-6"
                style={{ fontSize: 13 }}
                onClick={submitForm}
                disabled={
                  (values.campaignDataType === "json" && !values.recipients.length) ||
                  (values.campaignDataType === "tag" && contactsLoading) ||
                  !selectedBot ||
                  (values.campaignDataType === "file" && (fileDraft.previewStale || !fileDraft.preview?.canLaunch || !fileDraft.preview?.revision))
                }
              >
                {t(values.campaignDataType === "file" ? locale.campaignAudienceContinue : locale.next)}
              </button>
            </div>
            {serverError && <p role="alert" className="help is-danger has-text-right mt-2">{serverError}</p>}
          </>
        )}
      </Formik>
    </div>
  );
};

export default observer(UploadAudience);
